import type { Command, CommandMap } from '@/shared/commands/command.types'

import { ColumnType } from '~shared/rundown/column'
import type { MaybeCell } from '~shared/types/Cell'

import { useCellSnapshot } from '@/features/rundown/data/cells.store'

import { gridStateActions } from '../grid/grid.store'
import { modalActions } from '../modals/modal.store'

import { getCellActions } from './cell.actions'
import { useCellKey } from './cell.context'
import {
  useCellColumn,
  useCellIsEditing,
  useCellIsReadOnly,
} from './cell.hooks'
import type { CellKey } from './cell.schema'

export type CellCommandContext = {
  cellKey: CellKey
  cellId: NonNullable<MaybeCell>['id'] | undefined
  isSelectColumnCell: boolean
  isSparse: boolean
  isReadOnly: boolean
  isEditing: boolean
}

export function useCellCommandContext(): CellCommandContext {
  const cellKey = useCellKey()
  const cell = useCellSnapshot()
  const isReadOnly = useCellIsReadOnly()
  const isEditing = useCellIsEditing()
  const column = useCellColumn()

  return {
    cellKey: cellKey,
    cellId: cell?.id,
    isSelectColumnCell: column.type === ColumnType.SELECT,
    isSparse: !cell?.id,
    isReadOnly: isReadOnly,
    isEditing: isEditing,
  }
}

export const cellCommands = {
  edit: {
    id: 'cell.edit',
    label: 'Edit',
    shortcuts: ['Enter'],
    keywords: ['edit'],
    isEnabled: ({ isReadOnly }) => !isReadOnly, // Prevent when guest or locked, but allow sparse cells
    isVisible: ({ isEditing }) => !isEditing, // Show only in preview
    execute: ({ cellKey }) => getCellActions(cellKey).startEditMode(),
  },

  history: {
    id: 'cell.history',
    label: 'History…',
    shortcuts: [],
    keywords: ['edit', 'undo', 'redo', 'timeline'],
    isEnabled: ({ isReadOnly }) => !isReadOnly, // Prevent when guest or locked, but allow sparse cells
    isVisible: ({ isEditing, isSelectColumnCell }) =>
      !isEditing && !isSelectColumnCell, // History is only for text cell previews
    execute: ({ cellKey }) =>
      void modalActions.open('CELL_HISTORY', { cellKey }),
  },

  copy: {
    id: 'cell.copy',
    label: 'Copy',
    shortcuts: ['Mod+C'],
    keywords: ['clipboard'],
    isEnabled: ({ isSparse }) => !isSparse, // sparse cell have nothing to copy
    isVisible: ({ isSelectColumnCell }) => !isSelectColumnCell, // History is only for text cells
    isShortcutEnabled: ({ isEditing }) => !isEditing,
    execute: ({ cellKey }) => getCellActions(cellKey).copyContents(),
  },

  paste: {
    id: 'cell.paste',
    label: 'Paste',
    shortcuts: ['Mod+V'],
    keywords: ['clipboard'],
    isEnabled: ({ isReadOnly }) => !isReadOnly, // disallow guests to paste, allow sparse cells
    isVisible: ({ isSelectColumnCell }) => !isSelectColumnCell, // History is only for text cells
    isShortcutEnabled: ({ isEditing }) => !isEditing,
    execute: ({ cellKey }) => getCellActions(cellKey).pasteContents(),
  },

  clear: {
    id: 'cell.clear',
    label: 'Clear',
    shortcuts: ['Backspace', 'Delete'],
    keywords: ['edit'],
    isEnabled: ({ isReadOnly, isSparse }) => !isReadOnly && !isSparse,
    isVisible: ({ isSelectColumnCell }) => !isSelectColumnCell, // History is only for text cells
    execute: ({ cellKey }) => getCellActions(cellKey).deleteContents(),
  },

  discard: {
    id: 'cell.discard',
    label: 'Discard changes',
    shortcuts: ['Escape'],
    keywords: ['edit'],
    isEnabled: () => true, // Always enabled
    isVisible: ({ isEditing }) => isEditing, // Show only in editing
    execute: () => gridStateActions.editor.discard(),
  },
} satisfies CommandMap<CellCommandContext>

type SaveCommandOptions<CellCommandContext> = Pick<
  Command<CellCommandContext>,
  'execute' | 'isEnabled'
>

export function createSaveCellCommand<CellCommandContext>(
  options: SaveCommandOptions<CellCommandContext>,
): Command<CellCommandContext> {
  return {
    id: 'cell.save',
    label: 'Save changes',
    shortcuts: ['Mod+Enter'],
    keywords: ['edit'],
    ...options, // Local overrides
  }
}
